import { Stack } from "@chakra-ui/react";
import { InfiniteData } from "@tanstack/react-query";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { queryClient } from "../../../../api";
import { OperationItem } from "./OperationItem";
import { OperationListError } from "./OperationListError.tsx";
import { OperationListEmpty } from "./OperationListEmpty.tsx";
import { OperationListFooter } from "./OperationListFooter.tsx";
import { OperationListPending } from "./OperationListPending.tsx";
import { useOperationListFiltersStore } from "../../../../store/operationListFiltersStore.ts";
import { IOperationQueryParams, IOperationResponse } from "../../../../api/operations/types.ts";
import { operationsHistoryQueryKey_part1, useGetOperationsQuery } from "../../../../api/operations";

export function OperationList() {
  const filterAlias = useOperationListFiltersStore(state => state.filterAlias);
  const filterCodes = useOperationListFiltersStore(state => state.filterCodes);

  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const { queryKey, params } = useMemo(() => {
    const params: IOperationQueryParams = { type: filterCodes };
    const queryKey = [operationsHistoryQueryKey_part1, params] as const;

    return { queryKey, params };
  }, [filterCodes]);

  const {
    data,
    isPending,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useGetOperationsQuery(params);

  const allOperations = useMemo(() => {
    const months: Record<string, boolean> = {};

    return data?.pages.flatMap((page) => {
      if (!Array.isArray(page.content)) {
        return [];
      }

      return page.content.map((operation) => {
        const currentMonth = operation.date?.substring(0, 7);

        if (currentMonth && !months[currentMonth]) {
          months[currentMonth] = true;
          return { ...operation, isFirstInMonth: true };
        } else {
          return { ...operation, isFirstInMonth: false };
        }
      });
    }) ?? [];
  }, [data]);

  const loadMore = useCallback(() => {
    return hasNextPage && !isFetchingNextPage ? void fetchNextPage() : undefined;
  }, [hasNextPage, isFetchingNextPage]);

  // При размонтировании оставляем в кеше только 1ю страницу
  useEffect(() => {
    return () => {
      queryClient.setQueryData<InfiniteData<IOperationResponse>>(
        queryKey,
        (oldData) => {
          if (!oldData?.pages.length) {
            return undefined;
          }

          return {
            pages: oldData.pages.slice(0, 1),
            pageParams: oldData.pageParams.slice(0, 1),
          };
        });
    };
  }, [queryClient, queryKey]);

  // Сбрасываем положение скрола при смене фильтра
  useEffect(() => {
    const timer = setTimeout(() => {
      if (virtuosoRef.current) {
        virtuosoRef.current.scrollToIndex({
          index: 0,
          align: "start",
          behavior: "smooth",
        });
      }
    }, 0);

    return () => {
      clearTimeout(timer);
    };
  }, [filterAlias]);

  if (isPending) {
    return <OperationListPending />;
  }

  if (isError && allOperations.length === 0) {
    return <OperationListError refetch={() => void fetchNextPage()} />;
  }

  if (allOperations.length === 0) {
    return <OperationListEmpty filterAlias={filterAlias} />;
  }

  return (
    <Stack flex="1" pb="8px" pt="9px">
      <Virtuoso
        ref={virtuosoRef}
        style={{ flex: "1", paddingTop: "8px" }}
        increaseViewportBy={800}
        overscan={2}
        data={allOperations}
        itemContent={(_, item) => {
          return <OperationItem key={item.id} operation={item} />;
        }}
        endReached={loadMore}
        components={{
          Footer: () => (
            <OperationListFooter
              isFetching={isFetchingNextPage}
              isError={isError}
              count={allOperations.length}
              refetch={() => void fetchNextPage()}
            />
          ),
        }}
      />
    </Stack>
  );
}
